import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Copy, ExternalLink, Loader2, RefreshCw, Trash2 } from "lucide-react";

/**
 * Manage login-free "wall display" links for the Weekly Clips page.
 * Each link = a readable name the admin picks + a random code the app adds
 * (e.g. /wall/iowa-kpi-k7m2qx9p), locked to one location. Server side:
 * src/server/displayLinks.ts.
 */

const LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
type Location = (typeof LOCATIONS)[number];

/** Mirror of the server's normalizeLinkName, for the live preview. */
function previewName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

function wallUrl(slug: string): string {
  return `${window.location.origin}/wall/${slug}`;
}

export default function DisplayLinksDialog({
  open,
  onOpenChange,
  defaultLocation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultLocation: Location | null;
}) {
  const utils = trpc.useUtils();
  const linksQuery = trpc.admin.listDisplayLinks.useQuery(undefined, { enabled: open });
  const createMutation = trpc.admin.createDisplayLink.useMutation();
  const replaceMutation = trpc.admin.replaceDisplayLinkCode.useMutation();
  const deleteMutation = trpc.admin.deleteDisplayLink.useMutation();

  const [name, setName] = useState("");
  const [location, setLocation] = useState<Location>(defaultLocation ?? "Iowa");
  // Two-click confirm for the destructive actions: "<action>:<id>".
  const [confirming, setConfirming] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLocation(defaultLocation ?? "Iowa");
      setConfirming(null);
    }
  }, [open, defaultLocation]);

  const preview = previewName(name);

  const copy = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(wallUrl(slug));
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — select the link and copy it by hand.");
    }
  };

  const create = async () => {
    try {
      const link = await createMutation.mutateAsync({ name, location });
      setName("");
      await utils.admin.listDisplayLinks.invalidate();
      await copy(link.slug);
    } catch (err: any) {
      toast.error(err?.message ?? "Couldn't create the link");
    }
  };

  const replace = async (id: string) => {
    if (confirming !== `replace:${id}`) return setConfirming(`replace:${id}`);
    setConfirming(null);
    try {
      await replaceMutation.mutateAsync({ id });
      await utils.admin.listDisplayLinks.invalidate();
      toast.success("New code made — the old link no longer works. Update the wall's bookmark.");
    } catch (err: any) {
      toast.error(err?.message ?? "Couldn't replace the code");
    }
  };

  const remove = async (id: string) => {
    if (confirming !== `delete:${id}`) return setConfirming(`delete:${id}`);
    setConfirming(null);
    try {
      await deleteMutation.mutateAsync({ id });
      await utils.admin.listDisplayLinks.invalidate();
      toast.success("Link deleted — it no longer works.");
    } catch (err: any) {
      toast.error(err?.message ?? "Couldn't delete the link");
    }
  };

  const links = linksQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Wall display links</DialogTitle>
          <DialogDescription>
            A view-only link to the live Weekly Clips board for one location — no login, never expires. It shows
            provider names, photos and clip counts only (no patient names or footage). Anyone with the link can
            see it, so share it only with the wall screen. You can replace or delete a link anytime.
          </DialogDescription>
        </DialogHeader>

        {/* Existing links */}
        <div className="space-y-2">
          {linksQuery.isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : links.length === 0 ? (
            <p className="text-sm text-muted-foreground">No wall links yet — make one below.</p>
          ) : (
            links.map((l) => (
              <div key={l.id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold rounded bg-muted px-2 py-0.5">{l.location}</span>
                  <code className="text-xs break-all flex-1 select-all">{wallUrl(l.slug)}</code>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => void copy(l.slug)}>
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1" asChild>
                    <a href={wallUrl(l.slug)} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    disabled={replaceMutation.isPending}
                    onClick={() => void replace(l.id)}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    {confirming === `replace:${l.id}` ? "Click again — old link stops working" : "Replace code"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-red-600 hover:text-red-700"
                    disabled={deleteMutation.isPending}
                    onClick={() => void remove(l.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {confirming === `delete:${l.id}` ? "Click again to delete" : "Delete"}
                  </Button>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    Made {new Date(l.createdAt).toLocaleDateString()}
                    {l.createdByName ? ` by ${l.createdByName}` : ""}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* New link */}
        <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-2">
          <div className="text-sm font-medium">Make a new link</div>
          <div className="flex flex-wrap gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name it, e.g. Iowa KPI Wall"
              maxLength={80}
              className="flex-1 min-w-[200px] bg-background"
            />
            <Select value={location} onValueChange={(v) => setLocation(v as Location)}>
              <SelectTrigger className="w-[140px] bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((loc) => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => void create()} disabled={preview.length < 2 || createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Create link
            </Button>
          </div>
          <p className="text-xs text-muted-foreground break-all">
            {preview.length >= 2 ? (
              <>
                Will look like <code>{window.location.origin}/wall/{preview}-<span className="italic">xxxxxxxx</span></code>{" "}
                — the app adds the 8-character code so nobody can guess the address.
              </>
            ) : (
              "Use letters and numbers; spaces become dashes."
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
